import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  type QueryConstraint,
  type DocumentData,
  type WithFieldValue,
} from 'firebase/firestore'
import { db } from '../firebase/config'
import type { FirestoreDocument } from './firestoreTypes'

export class FirestoreClient {
  async getDocument<T extends FirestoreDocument>(
    collectionName: string,
    documentId: string
  ): Promise<T | null> {
    const docRef = doc(db, collectionName, documentId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as T
  }

  /**
   * Como `getDocument`, pero SIEMPRE contra el servidor (`getDocFromServer`):
   * para lecturas que deciden algo que no se puede decidir con una caché vacía
   * (el consentimiento — ADR-SEC-013 local). Sin red, rechaza.
   */
  async getDocumentFromServer<T extends FirestoreDocument>(
    collectionName: string,
    documentId: string
  ): Promise<T | null> {
    const docSnap = await getDocFromServer(doc(db, collectionName, documentId))

    if (!docSnap.exists()) {
      return null
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as T
  }

  async getDocuments<T extends FirestoreDocument>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> {
    const collectionRef = collection(db, collectionName)
    const q = constraints.length > 0 ? query(collectionRef, ...constraints) : collectionRef

    const querySnapshot = await getDocs(q)

    return querySnapshot.docs.map(
      (docSnap) =>
        ({
          id: docSnap.id,
          ...docSnap.data(),
        }) as T
    )
  }

  async getSubcollectionDocuments<T extends FirestoreDocument>(
    parentCollection: string,
    parentId: string,
    subcollectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> {
    const subcollectionRef = collection(db, parentCollection, parentId, subcollectionName)
    const q =
      constraints.length > 0 ? query(subcollectionRef, ...constraints) : subcollectionRef

    const querySnapshot = await getDocs(q)

    return querySnapshot.docs.map(
      (docSnap) =>
        ({
          id: docSnap.id,
          ...docSnap.data(),
        }) as T
    )
  }

  async documentExists(collectionName: string, documentId: string): Promise<boolean> {
    const docRef = doc(db, collectionName, documentId)
    const docSnap = await getDoc(docRef)
    return docSnap.exists()
  }

  async createDocument<T extends DocumentData>(
    collectionName: string,
    data: WithFieldValue<T>
  ): Promise<string> {
    const collectionRef = collection(db, collectionName)
    const docRef = await addDoc(collectionRef, data)
    return docRef.id
  }

  async setDocument<T extends DocumentData>(
    collectionName: string,
    documentId: string,
    data: WithFieldValue<T>,
    merge = false
  ): Promise<void> {
    const docRef = doc(db, collectionName, documentId)
    await setDoc(docRef, data as WithFieldValue<DocumentData>, { merge })
  }

  async updateDocument<T extends Partial<DocumentData>>(
    collectionName: string,
    documentId: string,
    data: WithFieldValue<T>
  ): Promise<void> {
    const docRef = doc(db, collectionName, documentId)
    await updateDoc(docRef, data as WithFieldValue<DocumentData>)
  }

  async deleteDocument(collectionName: string, documentId: string): Promise<void> {
    const docRef = doc(db, collectionName, documentId)
    await deleteDoc(docRef)
  }
}

export const firestoreClient = new FirestoreClient()
